import JSZip from "jszip";
import { useState } from "react";
import { observer } from "mobx-react-lite";
import { Button, Center, Flex, FlexProps, Spacer, Text, useToast } from "@chakra-ui/react";

import { APIOption, Dictionary, download, INode, Graph, Lang, toJsonBlob } from "@noli/core";
import { exportNodes } from "@noli/svg";
import { langStore, translate, useSelecting } from "@noli/business";

import type { MetaType } from "@/types/meta";
import { toast, useOwnToast } from "@/utils/misc";
import { Editor_Words } from "@/utils/lang/editor";
import { postOperation, SafeOp } from "@/utils/safeOp";
import { themeStore } from "@/stores/theme";
import SubEditorPanel from "@/components/SubPanel/SubEditor";
import SchemaProperty from "@/components/Property/SchemaProperty";

type ImageFormat = "JPG" | "PNG" | "SVG";
type DownloadFormat = ImageFormat | "NOLI";
const allDownloadFormats: DownloadFormat[] = ["JPG", "PNG", "SVG", "NOLI"];
const isImage = (format: DownloadFormat) => format === "JPG" || format === "PNG";

async function exportOne(
  t: ReturnType<typeof useToast>,
  lang: Lang,
  node: INode,
  format: ImageFormat,
  exportOriginal: boolean,
): Promise<Blob | void> {
  if (isImage(format) && node.type === "image" && exportOriginal) {
    let url = node.renderParams.src;
    if (format === "JPG") {
      if (url.startsWith("https://ailabcdn.nolibox.com/")) {
        url = `${url}?imageMogr2/format/jpeg`;
      } else if (url.startsWith("https://ailab-huawei-cdn.nolibox.com/")) {
        url = `${url}?x-image-process=image/format,jpeg`;
      }
    }
    return fetch(url).then((res) => res.blob());
  }
  const bounding = node.bbox.bounding.toAABB();
  const targetNodes = node.type === "group" ? node.allChildrenNodes : [node];
  if (isImage(format)) {
    return SafeOp.exportBlob(targetNodes, { t, lang, exportOptions: { exportBox: bounding } });
  }
  const res = await exportNodes(targetNodes, { exportBox: bounding });
  if (!res) {
    toast(t, "error", translate("export-blob-error-message", lang));
    return;
  }
  return toJsonBlob(res.svg.svg());
}

function DownloadSetting({
  lang,
  color,
  captionWord,
  value,
  choices,
  setValue,
  dictionary,
}: {
  lang: Lang;
  color: string;
  captionWord: string;
  value: string;
  choices: string[];
  setValue: (value: string) => void;
  dictionary?: Dictionary<string>;
}) {
  return (
    <Flex w="80%" mb="16px" align="center">
      <Text flex={1} color={color} userSelect="none" fontSize="12px">
        {translate(captionWord, lang)}
      </Text>
      <SchemaProperty
        identifier={captionWord}
        w="100px"
        pl="16px"
        pr="12px"
        lang={lang}
        apiKey={captionWord}
        apiOption={new APIOption("choice", value, { choices })}
        value={value}
        setValue={() => setValue}
        dictionary={dictionary as any}
      />
    </Flex>
  );
}

function DownloadPanel(props: FlexProps) {
  // 0: original ; 1: on board
  const sizes = [
    Editor_Words["download-image-size-original"],
    Editor_Words["download-image-size-on-board"],
  ];

  const t = useOwnToast();
  const lang = langStore.tgt;
  const { type, nodes } = useSelecting("raw");
  const basicInfo = useSelecting("basic")({ fixed: 2 });
  const { captionColor, separatorColor } = themeStore.styles;
  const [format, setFormat] = useState<DownloadFormat>("JPG");
  const [size, setSize] = useState(sizes[0] as string);

  if (type === "none") {
    return null;
  }

  function getUrl(node: INode): string | null | (string | null)[] {
    if (node.type === "group") {
      return node.allChildrenNodes.map((n) => getUrl(n) as string | null);
    }
    if (node.type === "image") {
      return node.renderParams.src;
    }
    return null;
  }
  async function onDownload(): Promise<void> {
    if (type === "none") return;
    toast(t, "success", translate(Editor_Words["downloading-message"], lang));
    if (format === "NOLI") {
      postOperation("download", {
        urls: nodes.map((node) => getUrl(node)),
        metas: nodes.map((node) =>
          node.type === "group"
            ? node.allChildrenNodes.map((n) => n.meta as MetaType)
            : (node.meta as MetaType),
        ),
      });
      const graph = Graph.fromNodes(nodes);
      await download(toJsonBlob(graph.toJsonInfo()), "exported.noli");
    } else {
      const blobs = await Promise.all(
        nodes.map((node) => exportOne(t, lang, node, format, size === sizes[0])),
      );
      const valid = blobs
        .map((blob, i) => ({ blob, node: nodes[i] }))
        .filter(({ blob }) => !!blob) as { blob: Blob; node: INode }[];
      if (valid.length === 0) {
        toast(t, "error", translate(Editor_Words["download-image-error-message"], lang));
        return;
      }
      postOperation("download", {
        urls: valid.map(({ node }) => getUrl(node)),
        metas: valid.map(({ node }) =>
          node.type === "group"
            ? node.allChildrenNodes.map((n) => n.meta as MetaType)
            : (node.meta as MetaType),
        ),
      });
      const appendix = format.toLowerCase();
      if (valid.length === 1) {
        await download(valid[0].blob, `exported.${appendix}`);
      } else {
        const zip = new JSZip();
        valid.forEach(({ blob }, i) => zip.file(`${i}.${appendix}`, blob, { binary: true }));
        const content = await zip.generateAsync({
          type: "blob",
          compression: "DEFLATE",
          compressionOptions: { level: 1 },
        });
        await download(content, "exported.zip");
      }
    }
  }

  const sizeDictionary: Dictionary<string> = {};
  sizes.forEach((size) => (sizeDictionary[size] = translate(size, lang)));
  let sizeString;
  if (type === "multiple") {
    sizeString = translate(Editor_Words["size-string-template"], lang).replace(
      "$1",
      nodes.length.toString(),
    );
  } else if (basicInfo) {
    const { type, w, h, imgWH } = basicInfo;
    if (type !== "image" || size === sizes[1]) {
      sizeString = `${w} x ${Math.abs(h)}`;
    } else {
      sizeString = imgWH ? `${imgWH.w} x ${imgWH.h}` : "loading...";
    }
  } else {
    return null;
  }

  return (
    <SubEditorPanel
      w="100%"
      // h="200px"
      minH="150px"
      borderTop="1px"
      borderColor={separatorColor}
      {...props}
    >
      <Center pt="24px" flexDirection="column">
        <DownloadSetting
          lang={lang}
          color={captionColor}
          captionWord={"download-format"}
          value={format}
          choices={allDownloadFormats}
          setValue={setFormat as any}
        />
        {format !== "NOLI" && (
          <DownloadSetting
            lang={lang}
            color={captionColor}
            captionWord={Editor_Words["download-image-size"]}
            value={size}
            choices={sizes}
            setValue={setSize}
            dictionary={sizeDictionary}
          />
        )}
        <Flex w="100%" h="54px" mt="-16px" align="center">
          <Spacer />
          <Text mr="36px" color={captionColor} fontSize={12}>
            {sizeString}
          </Text>
        </Flex>
      </Center>
      <Center mb="24px">
        <Button w="80%" onClick={onDownload} variant="outline">
          {translate("download", lang)}
        </Button>
      </Center>
    </SubEditorPanel>
  );
}

export default observer(DownloadPanel);
